import { Observable, of } from 'rxjs';

/** Represents a result which can succeed or error. */
export interface Result<T = object, E = string> {
  /** The successful result. */
  data?: T;

  /** The error message or payload. */
  error?: E;

  /** True if the result is a success. */
  success: boolean;

  /** Real HTTP status code. Example: 400 */
  httpStatus?: number;

  /** Backend/application status code from payload. Example: 404 */
  apiStatus?: number;

  /** Optional backend error key. */
  errorKey?: string;
}

/** Represents a successful result. */
export class SuccessResult<T = void> implements Result<T> {
  readonly success = true;

  constructor(
    public data?: T,
    public httpStatus?: number,
    public apiStatus?: number,
  ) {}
}

/** Represents an errored result. */
export class ErrorResult<T = string> implements Result<any, T> {
  readonly success = false;

  constructor(
    public error?: T,
    public httpStatus?: number,
    public apiStatus?: number,
    public errorKey?: string,
    public data?: any,
  ) {}
}

/** An observable which emits results of the given type. */
export class ObservableResult<T> extends Observable<Result<T>> {
  static ofSuccess<T = void>(
    response?: T,
    httpStatus?: number,
    apiStatus?: number,
  ): Observable<Result<T>> {
    return of(new SuccessResult(response, httpStatus, apiStatus));
  }

  static ofError(
    message: string,
    httpStatus?: number,
    apiStatus?: number,
    errorKey?: string,
    data?: any,
  ): Observable<Result<any>> {
    return of(new ErrorResult(message, httpStatus, apiStatus, errorKey, data));
  }
}
